import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable, filter, first } from 'rxjs';
import { PreferencesService, ThemeMode } from './preferences.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly themeClass = 'theme-light';
  private readonly storagePrefix = 'user-theme-mode:';
  private theme$ = new BehaviorSubject<ThemeMode>('dark');
  private renderer: Renderer2;

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private preferences: PreferencesService,
    private authService: AuthService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    const stored = this.loadStoredTheme();
    const initial = stored ?? this.detectPreferredColorScheme();
    this.applyThemeClass(initial);
    this.theme$.next(initial);

    this.preferences.getPreferences().subscribe((prefs) => {
      if (!prefs?.theme_mode) {
        return;
      }
      if (prefs.theme_mode !== this.theme$.value) {
        this.applyThemeClass(prefs.theme_mode);
        this.theme$.next(prefs.theme_mode);
      }
      this.storeTheme(prefs.theme_mode);
    });
  }

  get themeChanges(): Observable<ThemeMode> {
    return this.theme$.asObservable();
  }

  init(): void {
    if (this.authService.isAuthenticated()) {
      this.fetchPreferencesSafely();
      return;
    }

    this.authService.currentUser$
      .pipe(filter((user): user is NonNullable<typeof user> => !!user), first())
      .subscribe({
        next: () => this.fetchPreferencesSafely(),
        error: () => {}
      });
  }

  private fetchPreferencesSafely(): void {
    this.preferences.loadPreferences().subscribe({
      next: () => {},
      error: () => {}
    });
  }

  setTheme(mode: ThemeMode): void {
    if (mode === this.theme$.value) {
      this.storeTheme(mode);
      return;
    }
    this.applyThemeClass(mode);
    this.theme$.next(mode);
    this.storeTheme(mode);
    this.preferences.updatePreferences({ theme_mode: mode }).subscribe({
      error: (err) => {
        console.warn('[Theme] Theme preference konnte nicht gespeichert werden', err);
      }
    });
  }

  get currentTheme(): ThemeMode {
    return this.theme$.value;
  }

  private applyThemeClass(mode: ThemeMode): void {
    if (!this.document?.body) {
      return;
    }
    if (mode === 'light') {
      this.renderer.addClass(this.document.body, this.themeClass);
    } else {
      this.renderer.removeClass(this.document.body, this.themeClass);
    }
  }

  private detectPreferredColorScheme(): ThemeMode {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }

  private storageKey(): string {
    const userId = this.authService.getCurrentUser()?.id;
    return userId ? `${this.storagePrefix}${userId}` : `${this.storagePrefix}global`;
  }

  private loadStoredTheme(): ThemeMode | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      const stored = localStorage.getItem(this.storageKey());
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // ignore
    }
    return null;
  }

  private storeTheme(mode: ThemeMode): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      localStorage.setItem(this.storageKey(), mode);
    } catch {
      // ignore
    }
  }
}
